// What Kin can SAY about her day, out loud, mid-call — issue #1686.
//
// ── The gap this closes ──────────────────────────────────────────────────────
//
// Voice shipped with one read tool, search_web, and nothing that could look at
// her own data. Everything it knew about her came from a ~150-token family
// summary baked in when the session token was minted. So "what's on today?" —
// the single most natural thing to ask hands-free, in a car, which is exactly
// where voice is worth more than chat — was answered from nothing. The persona
// said so honestly ("you can't see her calendar or her tasks"), which kept it a
// gap rather than a defect. Honest is not the same as useful.
//
// ── Why the shaping lives here and not in the hook ───────────────────────────
//
// Same split as voiceSearchOutcome.ts, voiceCaptureGate.ts and voiceSearchCap.ts:
// pure, Deno-free, no client, so the ordinary vitest suite covers it by relative
// import and nothing has to mock a WebRTC data channel to test what Kin will
// say. voiceScheduleRead.ts owns the queries; this owns the shape.
//
// ── The three things this file is actually for ───────────────────────────────
//
// 1. RESOLVING THE WINDOW, so the model never does calendar arithmetic. The
//    tool takes an enum ("today" / "tomorrow" / "week"), not a date. A date
//    parameter would mean the model computing days against a "today" it was only
//    told about in a prompt — the failure create_event's own description warns
//    about twice and which executeCreateEventTool carries a past-date guard for.
//
// 2. MAKING IT SPEAKABLE. "18:00:00" is a TEXT column that a speech model reads
//    aloud as digits; 2026-09-24 is worse. Every time and day in the payload is
//    pre-shaped into words, so Kin never has to reformat and never has to guess.
//
// 3. TELLING THE MODEL WHAT IT MAY NOT SAY. An empty day is the one that needs
//    this most — see EMPTY_WINDOW_RULE.
//
// ⚠️ Deliberately NOT here: any URL, any row id, any recipient phone number or
// email address. The #887 invariant is that the speech model is given only what
// it is safe to say out loud, and a Ready-to-Fire card's whole payload is a
// drafted message to a named person. She gets the TITLE of the card. The body
// stays in her Tasks tab, where she taps it.

// #1706 — the ONE completion predicate, imported rather than re-written. This
// module had no imports at all, and a two-line `row.is_completed === true` here
// would have been the cheapest thing to write and the fourth copy of a rule
// #1696 exists to stop copying. Same module voiceScheduleRead already imports,
// no Deno APIs in it.
import { isEventCompleted } from "../../supabase/functions/_shared/eventVisibility";

/** The windows the model may ask for. An enum, never a date — see the header. */
export const VOICE_SCHEDULE_RANGES = ["today", "tomorrow", "week"] as const;
export type VoiceScheduleRange = (typeof VOICE_SCHEDULE_RANGES)[number];

export function isVoiceScheduleRange(raw: unknown): raw is VoiceScheduleRange {
  return typeof raw === "string" && (VOICE_SCHEDULE_RANGES as readonly string[]).includes(raw);
}

/**
 * How many days "week" covers, counting today.
 *
 * Seven, not "the rest of the calendar week". "What does my week look like" on a
 * Saturday means the next seven days, not the two remaining ones — a
 * calendar-week window answers a question nobody asked and reads as Kin having
 * lost half her week.
 */
export const VOICE_WEEK_DAYS = 7;

/**
 * Payload caps. A voice turn is one or two sentences; anything past this is
 * tokens the model pays for and then does not say.
 *
 * ⚠️ The caps CUT, and a cut that is not reported is a lie by omission — she
 * asks what her week looks like, hears four things, and plans around a week that
 * had nine in it. So `more_events` / `more_tasks` travel with the payload and
 * the guidance tells the model to mention them.
 */
export const MAX_SPOKEN_EVENTS = 12;
export const MAX_SPOKEN_TASKS = 8;

/** The event columns this module needs. A subset of `events`, on purpose. */
export interface VoiceScheduleEventRow {
  title: string | null;
  event_date: string | null;
  start_time: string | null;
  is_all_day: boolean | null;
  location: string | null;
  child: string | null;
  /**
   * #1706 — she ticked it off on her Today tab. Optional because rows reach this
   * module through a cast (`data as VoiceScheduleEventRow[]`), so a required
   * field would not catch a reader that forgot the column anyway — what does is
   * COMPLETED_DECISIONS in eventVisibilityCoverage.test.ts, which asserts this
   * surface's select list still asks for it.
   */
  is_completed?: boolean | null;
}

/** The task_queue_items columns this module needs. No body, no recipient. */
export interface VoiceScheduleTaskRow {
  title: string | null;
  action_type: string | null;
  send_by: string | null;
}

export interface SpokenEvent {
  title: string;
  /** "at 6 PM" · "all day" · "" when the time is genuinely unknown. */
  when: string;
  location?: string;
  child?: string;
  /**
   * #1706 — present and true ONLY for an event she has already checked off.
   *
   * ⚠️ Omitted rather than `done: false` on every other event. A realtime model
   * handed a false flag on nine rows starts narrating it ("and that one's not
   * done yet"), which is both noise and a claim nobody made.
   */
  done?: true;
}

export interface SpokenTask {
  title: string;
  /** What kind of card it is, in words she'd recognise. */
  kind: string;
}

export interface SpokenDay {
  date: string;
  /** "today" · "tomorrow" · "Thursday". What Kin should actually say. */
  label: string;
  events: SpokenEvent[];
  tasks: SpokenTask[];
}

export interface VoiceScheduleResult {
  ok: true;
  range: VoiceScheduleRange;
  /** Her date, on her clock. Present so the model never has to work one out. */
  today: string;
  days: SpokenDay[];
  event_count: number;
  task_count: number;
  more_events: number;
  more_tasks: number;
  /** Present and true only when her Tasks tab could not be read at all. */
  tasks_unreadable?: boolean;
  guidance: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// The rules. Instructions TO the model, not copy she hears — it phrases them in
// Kin's own voice.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * ⚠️ The most important string in this file.
 *
 * A model handed an empty list hedges. Observed shapes, all wrong: "I'm not
 * seeing anything, but I might not have everything", "let me double-check",
 * "I can't see your calendar". The first two teach her the answer is unreliable
 * and she goes and looks anyway, which is the whole value of asking out loud
 * gone. The third is #1690's defect in a new place — denying a capability while
 * exercising it.
 *
 * An empty calendar is a REAL, GOOD answer and one of the nicer things Kin can
 * tell a mom. It has to be said like one.
 */
export const EMPTY_WINDOW_RULE =
  "You looked and there is genuinely nothing there — no events on her calendar and nothing waiting in her Tasks tab. " +
  "That is a real answer and a good one: tell her she's clear, warmly and briefly. " +
  "Do NOT hedge, do NOT offer to check again, do NOT say you can't see her calendar or that you might be missing something. You just looked.";

/** The ordinary case. Short, human, and nothing invented. */
export const SPOKEN_SCHEDULE_RULE =
  "This is her real calendar and her real Tasks tab. Say the two or three that matter in a sentence or two, with times, the way a friend reading it off her fridge would — never a list read out in order, never every field. " +
  "Say NOTHING that is not in here: no times you did not get, no events you do not see, and never guess at what something is.";

/** Appended when a cap cut the payload. */
export const TRUNCATED_RULE =
  "There is more than you were given. Say the ones that matter and tell her roughly how many others there are, so she knows you are summarising rather than listing everything.";

/**
 * Appended when her Tasks tab could not be read — issue #1686, found by Copilot
 * on #1695.
 *
 * ⚠️ Without this, an unreadable task table and an empty one produce the SAME
 * answer. With no events either, EMPTY_WINDOW_RULE then tells Kin to say
 * confidently that there is "nothing waiting in her Tasks tab" over a table
 * nobody could read — the read-failure-as-empty-calendar defect this module was
 * careful about for events, landing on tasks instead.
 */
export const TASKS_UNREADABLE_RULE =
  "⚠️ Her CALENDAR is what you see below and it is accurate. Her Tasks tab could NOT be read this time — " +
  "that is a hiccup on your end, not an empty list. Answer about her calendar, and if she asks about her tasks or cards, " +
  "say you couldn't pull those up this second and offer to try again. NEVER say she has nothing waiting.";

/**
 * Appended when something in the payload is already done — issue #1706.
 *
 * ⚠️ Voice gets the MARK, not the exclusion, and this rule is why that is safe.
 * Her 7am note and her prep offers drop completed events because they describe
 * what is ahead; voice is answering a question she just asked out loud, and at
 * 4pm "you had the dentist at nine, that's done" is the true answer. Dropping it
 * would make Kin silent about her own morning and would make "did I already do
 * that?" unanswerable out loud, which is the mode where she cannot go look.
 */
export const COMPLETED_RULE =
  "Anything marked done she has already checked off herself. Say so in the past tense if it comes up (\"the dentist this morning — that's done\"), " +
  "never as something still ahead of her, and never offer to help her get ready for it.";

/** Appended for any window wider than a single day. */
export const MULTI_DAY_RULE =
  "Lead with the soonest. Use the day names you were given — say \"Thursday\", never a date read out as numbers.";

// ─────────────────────────────────────────────────────────────────────────────
// Window resolution. All arithmetic is on a bare YYYY-MM-DD calendar date, in
// UTC, DELIBERATELY: the string has already been resolved on her wall clock by
// the caller, so re-introducing a zone here could only move it back off her day.
// Same reasoning as rtfSegments.parseSendByLocalDate.
// ─────────────────────────────────────────────────────────────────────────────

const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A bare calendar date as a UTC instant, or null if it is not one. */
export function parseYmd(ymd: string): Date | null {
  const m = YMD_RE.exec(String(ymd ?? "").trim());
  if (!m) return null;
  const instant = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  // Date.UTC NORMALIZES an impossible date — 2026-02-30 becomes March 2 — so the
  // parse is only trustworthy if it formats back to what came in. Same guard as
  // createEventBuilder.validEventDate, and the same reason.
  return instant.toISOString().slice(0, 10) === m[0] ? instant : null;
}

/**
 * The bare calendar day a stored value refers to, or "" if it has none.
 *
 * ⚠️ `events.event_date` is a real `date` column, but `task_queue_items.send_by`
 * is not reliably one in practice — rtfSegments.parseSendByLocalDate carries a
 * fallback for an ISO datetime landing there, so some rows do. An exact string
 * comparison against "YYYY-MM-DD" would drop those rows SILENTLY, and a dropped
 * card is a thing she was waiting on that Kin did not mention. Take the leading
 * calendar day and compare that.
 *
 * Deliberately does NOT parse-and-reformat: `new Date("2026-09-22T23:00:00")`
 * is local-time and `new Date("2026-09-22")` is UTC, so round-tripping a value
 * through a Date can move it a day. The leading ten characters of either shape
 * are already her day as stored.
 */
export function ymdOf(raw: unknown): string {
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(raw ?? "").trim());
  return m ? m[1] : "";
}

export function ymdPlusDays(ymd: string, days: number): string {
  const base = parseYmd(ymd);
  if (!base) return ymd;
  return new Date(base.getTime() + days * 86400000).toISOString().slice(0, 10);
}

export interface VoiceScheduleWindow {
  startYmd: string;
  endYmd: string;
  /** Every date in the window, in order. Drives the day grouping. */
  dates: string[];
}

export function resolveVoiceScheduleWindow(
  range: VoiceScheduleRange,
  todayYmd: string,
): VoiceScheduleWindow {
  const offset = range === "tomorrow" ? 1 : 0;
  const dayCount = range === "week" ? VOICE_WEEK_DAYS : 1;
  const startYmd = ymdPlusDays(todayYmd, offset);
  const dates: string[] = [];
  for (let i = 0; i < dayCount; i++) dates.push(ymdPlusDays(startYmd, i));
  return { startYmd, endYmd: dates[dates.length - 1], dates };
}

// ─────────────────────────────────────────────────────────────────────────────
// Speakable shaping.
// ─────────────────────────────────────────────────────────────────────────────

const WEEKDAY_FMT = new Intl.DateTimeFormat("en-US", { weekday: "long", timeZone: "UTC" });

/**
 * "today" · "tomorrow" · "Thursday".
 *
 * ⚠️ `timeZone: "UTC"` on the formatter is not a bug and is not a zone decision.
 * The date is already HER day as a bare string; parseYmd pins it to UTC
 * midnight, so formatting it in any other zone would name the weekday of the day
 * before.
 */
export function spokenDayLabel(ymd: string, todayYmd: string): string {
  if (ymd === todayYmd) return "today";
  if (ymd === ymdPlusDays(todayYmd, 1)) return "tomorrow";
  const instant = parseYmd(ymd);
  return instant ? WEEKDAY_FMT.format(instant) : ymd;
}

/**
 * "6 PM" · "6:30 AM" · "noon" · "midnight", from the "HH:MM:00" TEXT column.
 *
 * A speech model handed "18:00:00" reads digits. It is also the column every
 * reader in the app does `String(start_time).slice(0, 5)` on, so a malformed
 * value has always been possible — an unparseable one returns "" and the caller
 * treats the event as having no stated time rather than saying something wrong.
 */
export function spokenClock(raw: string | null): string {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(raw ?? "").trim());
  if (!m) return "";
  const hours = Number(m[1]);
  const minutes = Number(m[2]);
  if (!Number.isInteger(hours) || hours < 0 || hours > 23) return "";
  if (!Number.isInteger(minutes) || minutes < 0 || minutes > 59) return "";
  if (hours === 12 && minutes === 0) return "noon";
  if (hours === 0 && minutes === 0) return "midnight";
  const suffix = hours < 12 ? "AM" : "PM";
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return minutes === 0 ? `${hour12} ${suffix}` : `${hour12}:${String(minutes).padStart(2, "0")} ${suffix}`;
}

/**
 * ⚠️ `all_day` and a missing time are DIFFERENT states and must not collapse.
 * That collapse is bug #607, which five write sites made independently, and
 * createEventBuilder.timeColumnsForMode exists to keep them apart. Here it
 * matters because "all day" is a fact Kin can say and an unknown time is a
 * question she should ask — saying "all day" over a TBD event tells her the time
 * is handled when it is blank.
 */
export function spokenEventWhen(row: VoiceScheduleEventRow): string {
  if (row.is_all_day) return "all day";
  const clock = spokenClock(row.start_time);
  return clock ? `at ${clock}` : "";
}

/**
 * What a Ready-to-Fire card IS, in words she would recognise.
 *
 * Mapped from `action_type` rather than spoken raw: the column holds values like
 * `text_message` and `deeplink_order`, and a model handed those either reads the
 * underscore or invents a meaning for it.
 */
const TASK_KIND_BY_ACTION: Record<string, string> = {
  text_message: "a text to send",
  text: "a text to send",
  email: "an email to send",
  invite: "an invite to send",
  call: "a call to make",
  deeplink_order: "something to order",
  deeplink: "something to open",
  purchase: "something to order",
  todo: "a to-do",
};

export function spokenTaskKind(actionType: string | null): string {
  const key = String(actionType ?? "").trim().toLowerCase();
  // Falls back to the generic rather than to the raw value: "a card waiting"
  // is true of every row in this table, so an unmapped action_type degrades to
  // something vaguer instead of to something unspeakable.
  return TASK_KIND_BY_ACTION[key] ?? "a card waiting in her Tasks tab";
}

function cleanTitle(raw: string | null): string {
  return typeof raw === "string" ? raw.trim() : "";
}

export interface BuildVoiceScheduleInput {
  range: VoiceScheduleRange;
  /** Her date, already resolved on her wall clock by the caller. */
  todayYmd: string;
  events: VoiceScheduleEventRow[];
  tasks: VoiceScheduleTaskRow[];
  /**
   * The task read failed. NOT the same as no tasks: an empty list is a fact
   * about her day, and this is the absence of one. See TASKS_UNREADABLE_RULE.
   */
  tasksUnreadable?: boolean;
}

export function buildVoiceScheduleResult(input: BuildVoiceScheduleInput): VoiceScheduleResult {
  const { range, todayYmd } = input;
  const window = resolveVoiceScheduleWindow(range, todayYmd);
  const inWindow = new Set(window.dates);

  // Rows are filtered to the window HERE as well as in the query. Not
  // belt-and-braces for its own sake: the query bounds by date range, and a row
  // whose `event_date` is null or malformed would otherwise be grouped under a
  // day that does not exist in this window and reported in a count she could not
  // account for.
  // ⚠️ Normalize and filter ONCE, then slice, and derive the dropped count from
  // the same array. Filtering twice — once for the payload and once for the
  // count — is how a count and a message come to disagree about what she can
  // see, which is #1690 exactly, and Copilot caught that same shape in its first
  // attempt at that fix.
  const keptEvents = input.events
    .map((row) => ({ ymd: ymdOf(row.event_date), row }))
    .filter((e) => cleanTitle(e.row.title) !== "" && inWindow.has(e.ymd));
  const events = keptEvents.slice(0, MAX_SPOKEN_EVENTS);
  const droppedEvents = keptEvents.length - events.length;

  const keptTasks = input.tasks
    .map((row) => ({ ymd: ymdOf(row.send_by), row }))
    .filter((t) => cleanTitle(t.row.title) !== "" && inWindow.has(t.ymd));
  const tasks = keptTasks.slice(0, MAX_SPOKEN_TASKS);
  const droppedTasks = keptTasks.length - tasks.length;

  const days: SpokenDay[] = [];
  for (const date of window.dates) {
    const dayEvents = events
      .filter((e) => e.ymd === date)
      .map(({ row }) => {
        const spoken: SpokenEvent = { title: cleanTitle(row.title), when: spokenEventWhen(row) };
        const location = cleanTitle(row.location);
        if (location) spoken.location = location;
        const child = cleanTitle(row.child);
        if (child) spoken.child = child;
        // #1706 — the shared predicate, not `!!row.is_completed`: only a positive
        // true is done, so a row read without the column is never marked.
        if (isEventCompleted(row)) spoken.done = true;
        return spoken;
      });
    const dayTasks = tasks
      .filter((t) => t.ymd === date)
      .map(({ row }) => ({ title: cleanTitle(row.title), kind: spokenTaskKind(row.action_type) }));

    // An empty day is OMITTED from a multi-day window and KEPT for a single-day
    // one. "Thursday: nothing" seven times is noise the model then reads out;
    // but a "today" request with no rows must still come back as a day, or the
    // empty answer has nothing to be about.
    if (range === "week" && dayEvents.length === 0 && dayTasks.length === 0) continue;
    days.push({ date, label: spokenDayLabel(date, todayYmd), events: dayEvents, tasks: dayTasks });
  }

  const tasksUnreadable = input.tasksUnreadable === true;
  const empty = events.length === 0 && tasks.length === 0;
  const truncated = droppedEvents > 0 || droppedTasks > 0;
  // #1706 — derived from the SLICED payload, not the whole read. The rule
  // explains a mark; if the cap cut the only completed event, there is no mark
  // left to explain and the sentence would be talking about nothing.
  const anyDone = events.some(({ row }) => isEventCompleted(row));
  // ⚠️ `empty` may only claim the WHOLE window is clear when both halves were
  // actually read. An unreadable task table with no events is not an empty day,
  // and EMPTY_WINDOW_RULE forbids exactly the hedging that would otherwise be
  // the honest answer there.
  const guidance = empty && !tasksUnreadable
    ? EMPTY_WINDOW_RULE
    : [
        tasksUnreadable ? TASKS_UNREADABLE_RULE : "",
        SPOKEN_SCHEDULE_RULE,
        anyDone ? COMPLETED_RULE : "",
        range === "week" ? MULTI_DAY_RULE : "",
        truncated ? TRUNCATED_RULE : "",
      ]
        .filter(Boolean)
        .join(" ");

  return {
    ok: true,
    range,
    today: todayYmd,
    days,
    event_count: events.length,
    task_count: tasks.length,
    ...(tasksUnreadable ? { tasks_unreadable: true } : {}),
    more_events: droppedEvents,
    more_tasks: droppedTasks,
    guidance,
  };
}
